import { z } from 'zod';
import { isoDateSchema, montantSchema, nullableText, paginationSchema, round2 } from './common.ts';
import { methodePaiementSchema } from './paiement.ts';

/**
 * Vente au comptoir.
 *
 * Une commande vit dans le temps : elle attend le verrier, se règle en
 * plusieurs fois, peut rester impayée. Une vente au comptoir n'a rien de tout
 * cela — elle est payée et terminée dans le même geste. C'est cette différence
 * de cycle de vie, et non l'encombrement de l'écran, qui justifie un modèle
 * distinct : les mêler obligerait à neutraliser à la main tout ce qui fait une
 * commande, requête par requête.
 */

const PREFIXE_NUMERO = 'VTE';
const LONGUEUR_NUMERO = 4;

/** `VTE-2026-0001`. Série volontairement distincte de celle des factures. */
export function formatNumeroVente(annee: number, numeroOrdre: number): string {
  return `${PREFIXE_NUMERO}-${annee}-${String(numeroOrdre).padStart(LONGUEUR_NUMERO, '0')}`;
}

/* ------------------------------------------------------------------ */
/* Lignes                                                              */
/* ------------------------------------------------------------------ */

export const venteLigneInputSchema = z.object({
  /** null => ligne libre : facturée, mais sans mouvement de stock. */
  product_id: z.string().nullable().default(null),
  designation: z
    .string()
    .trim()
    .min(1, 'La désignation est obligatoire')
    .max(200, 'La désignation est trop longue'),
  quantite: z.coerce
    .number()
    .int('La quantité doit être un nombre entier')
    .min(1, 'La quantité doit être au moins 1')
    .max(9999, 'Quantité irréaliste'),
  prix_unitaire: montantSchema,
  taux_tva: z.coerce.number().min(0).max(1).default(0.2),
});
export type VenteLigneInput = z.infer<typeof venteLigneInputSchema>;

export type VenteLigne = {
  id: string;
  product_id: string | null;
  designation: string;
  quantite: number;
  prix_unitaire: number;
  taux_tva: number;
  total_ligne: number;
};

/* ------------------------------------------------------------------ */
/* Vente                                                               */
/* ------------------------------------------------------------------ */

export const MAX_LIGNES_VENTE = 50;

export const venteInputSchema = z.object({
  date_vente: isoDateSchema,
  lignes: z
    .array(venteLigneInputSchema)
    .min(1, 'Ajoutez au moins un article')
    .max(MAX_LIGNES_VENTE, `Maximum ${MAX_LIGNES_VENTE} articles par vente`),
  methode: methodePaiementSchema.nullable().default(null),
  client_id: z.string().nullable().default(null),
  client_nom: nullableText,
  note: nullableText,
  /**
   * Clé fournie par l'écran, identique si l'utilisateur clique deux fois.
   * Sur une connexion lente, un double envoi vaudrait deux ventes et deux
   * sorties de stock ; le serveur reconnaît la seconde et rend la première.
   */
  cle_idempotence: z
    .string()
    .trim()
    .min(8, "Clé d'idempotence trop courte")
    .max(64, "Clé d'idempotence trop longue")
    .nullable()
    .default(null),
});
export type VenteInput = z.infer<typeof venteInputSchema>;

export const annulerVenteSchema = z.object({
  motif: z
    .string()
    .trim()
    .min(3, 'Indiquez le motif de l’annulation')
    .max(200, 'Motif trop long'),
});
export type AnnulerVenteInput = z.infer<typeof annulerVenteSchema>;

export type Vente = {
  id: string;
  numero_ordre: number;
  numero: string;
  date_vente: string;
  total: number;
  methode: string | null;
  client_id: string | null;
  client_nom: string | null;
  vendeur: string | null;
  note: string | null;
  facture_id: string | null;
  annulee_at: string | null;
  motif_annulation: string | null;
  created_at: string;
  lignes: VenteLigne[];
};

export const venteListQuerySchema = paginationSchema.extend({
  q: z.string().trim().optional(),
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  /** Inclut les ventes annulées, exclues par défaut. */
  annulees: z.coerce.boolean().default(false),
});
export type VenteListQuery = z.infer<typeof venteListQuerySchema>;

/* ------------------------------------------------------------------ */
/* Le registre unifié                                                  */
/* ------------------------------------------------------------------ */

/**
 * Origine d'une ligne du registre des ventes.
 *
 * Confirmer une commande, c'est bien vendre — mais l'inscrire une seconde fois
 * placerait le même argent dans deux tables, et le chiffre d'affaires
 * doublerait. Le registre réunit donc les deux sources **à la lecture**, sans
 * rien recopier.
 */
export const ORIGINES_VENTE = ['COMPTOIR', 'COMMANDE'] as const;
export const origineVenteSchema = z.enum(ORIGINES_VENTE);
export type OrigineVente = z.infer<typeof origineVenteSchema>;

export const ORIGINE_VENTE_LABELS: Record<OrigineVente, string> = {
  COMPTOIR: 'Comptoir',
  COMMANDE: 'Commande',
};

export type LigneRegistre = {
  origine: OrigineVente;
  id: string;
  date: string;
  reference: string | null;
  libelle: string;
  total: number;
  /** Somme encaissée. Une vente au comptoir est réglée d'un coup. */
  encaisse: number;
  statut: string | null;
};

export const registreQuerySchema = paginationSchema.extend({
  q: z.string().trim().optional(),
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  origine: origineVenteSchema.optional(),
});
export type RegistreQuery = z.infer<typeof registreQuerySchema>;

/* ------------------------------------------------------------------ */
/* Calcul                                                              */
/* ------------------------------------------------------------------ */

/** Total d'une vente. Arrondi commercial ligne par ligne, puis au total (règle 4). */
export function calculerTotalVente(
  lignes: readonly { quantite: number; prix_unitaire: number }[],
): number {
  return round2(lignes.reduce((s, l) => s + round2(l.quantite * l.prix_unitaire), 0));
}

export function totalLigneVente(quantite: number, prixUnitaire: number): number {
  return round2(quantite * prixUnitaire);
}
